import { getToken, clearToken } from '../utils/authToken';
import { isOwnerAdminEmail } from '../utils/isOwnerAdmin';
import { useState, useEffect } from 'react';
import { API_URL, API } from '../config';
import { X, Settings, Bell, BellRing, Sparkles, Users, Shield, Tag, Save, Share2, Github, Brain, ChevronRight, MessageSquare, FileText, GraduationCap, Activity, User, BookOpen, ArrowLeft, HelpCircle, CreditCard } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useOnboarding } from '../context/OnboardingContext';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { NotificationSettings } from './DamyxNotifications';
import FAQModal from './FAQModal';
import MySubscriptionPanel from './MySubscriptionPanel';

const SETTINGS_KEY = 'damyxia-notification-settings';

const defaultSettings = {
  projectUpdates: true,
  newFeatures: true,
  communityHighlights: false,
  securityAlerts: true,
  marketing: false
};

const SettingsModal = ({ isOpen, onClose, onLogout, onOpenShare, onOpenGitHub, onOpenMemory, onOpenLearning, onOpenInsights, onOpenConversations, onOpenProfile, onOpenAdmin, onOpenTermsAdmin }) => {
  const { language } = useLanguage();
  const { resetOnboarding } = useOnboarding();
  const [settings, setSettings] = useState(defaultSettings);
  const [activeSection, setActiveSection] = useState('main');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userInfo, setUserInfo] = useState({ email: '', avatar: null });
  // CUADRO NEGRO feb-2026: Preguntas Frecuentes
  const [showFAQ, setShowFAQ] = useState(false);

  const txt = {
    es: {
      title: 'Configuracion',
      subtitle: 'Gestiona tu cuenta y preferencias',
      // Onboarding
      onboarding: 'Ver Incorporación',
      onboardingDesc: 'Recorre el tour guiado de DamyxIA',
      // User Profile section
      userProfile: 'Tu Perfil',
      editProfile: 'Editar perfil',
      // Quick Actions
      quickActions: 'Acciones rápidas',
      conversations: 'Conversaciones',
      conversationsDesc: 'Ver tus conversaciones guardadas',
      profile: 'Mi Perfil',
      profileDesc: 'Editar tu foto, contraseña y preferencias',
      share: 'Compartir',
      shareDesc: 'Comparte DamyxIA y gana recompensas',
      github: 'GitHub',
      githubDesc: 'Conecta tu cuenta de GitHub',
      memory: 'Memoria Contextual',
      memoryDesc: 'Configura lo que DamyxIA recuerda',
      learning: 'Modo Aprendizaje',
      learningDesc: 'Preferencias de comunicación persistentes',
      insights: 'Panel de Interacciones',
      insightsDesc: 'Visualiza tu evolución e interacciones',
      faq: 'Preguntas Frecuentes — Ayuda',
      faqDesc: 'Resuelve dudas sobre el funcionamiento de DamyxIA',
      admin: 'Administración',
      adminDesc: 'Gestionar usuarios de DamyxIA',
      terms: 'Términos y Condiciones',
      termsDesc: 'Gestionar versiones de términos',
      // Notifications section
      notifications: 'Notificaciones',
      notificationsDesc: 'Gestiona tus preferencias de email',
      // 🟥 Cuadro Negro 2026-02-20 — Mi Suscripción
      mySubscription: 'Mi Suscripción',
      mySubscriptionDesc: 'Gestioná tu plan Premium y facturación',
      pushNotifications: 'Notificaciones Push',
      pushNotificationsDesc: 'Recibe alertas instantáneas en tu navegador',
      projectUpdates: 'Actualizaciones de proyectos',
      projectUpdatesDesc: 'Recibe notificaciones cuando tus proyectos se actualicen',
      newFeatures: 'Nuevas funciones',
      newFeaturesDesc: 'Sé el primero en enterarte de las mejoras',
      communityHighlights: 'Lo más destacado',
      communityHighlightsDesc: 'Resumen semanal de la comunidad',
      securityAlerts: 'Alertas de seguridad',
      securityAlertsDesc: 'Notificaciones importantes de tu cuenta',
      marketing: 'Marketing',
      marketingDesc: 'Ofertas y contenido promocional',
      // Actions
      savePreferences: 'Guardar preferencias',
      logout: 'Cerrar sesión',
      saved: '¡Preferencias guardadas!',
      back: 'Volver',
      goBack: 'Regresar',
    },
    en: {
      title: 'Settings',
      subtitle: 'Manage your account and preferences',
      // Onboarding
      onboarding: 'View Onboarding',
      onboardingDesc: 'Take the DamyxIA guided tour',
      // User Profile section
      userProfile: 'Your Profile',
      editProfile: 'Edit profile',
      quickActions: 'Quick Actions',
      conversations: 'Conversations',
      conversationsDesc: 'View your saved conversations',
      profile: 'My Profile',
      profileDesc: 'Edit your photo, password and preferences',
      share: 'Share',
      shareDesc: 'Share DamyxIA and earn rewards',
      github: 'GitHub',
      githubDesc: 'Connect your GitHub account',
      memory: 'Contextual Memory',
      memoryDesc: 'Configure what DamyxIA remembers',
      learning: 'Learning Mode',
      learningDesc: 'Persistent communication preferences',
      insights: 'Insights Dashboard',
      insightsDesc: 'Visualize your evolution and interactions',
      faq: 'Frequently Asked Questions — Help',
      faqDesc: 'Get answers about how DamyxIA works',
      admin: 'Administration',
      adminDesc: 'Manage DamyxIA users',
      terms: 'Terms and Conditions',
      termsDesc: 'Manage terms versions',
      notifications: 'Notifications',
      notificationsDesc: 'Manage your email preferences',
      // 🟥 Cuadro Negro 2026-02-20 — Mi Suscripción
      mySubscription: 'My Subscription',
      mySubscriptionDesc: 'Manage your Premium plan and billing',
      pushNotifications: 'Push Notifications',
      pushNotificationsDesc: 'Receive instant alerts in your browser',
      projectUpdates: 'Project updates',
      projectUpdatesDesc: 'Get notified when your projects update',
      newFeatures: 'New features',
      newFeaturesDesc: 'Be the first to know about improvements',
      communityHighlights: 'Highlights',
      communityHighlightsDesc: 'Weekly community summary',
      securityAlerts: 'Security alerts',
      securityAlertsDesc: 'Important account notifications',
      marketing: 'Marketing',
      marketingDesc: 'Offers and promotional content',
      savePreferences: 'Save preferences',
      logout: 'Log out',
      saved: 'Preferences saved!',
      back: 'Back',
      goBack: 'Go back',
    }
  };

  const t = txt[language] || txt.es;

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      // 🟥 CUADRO NEGRO 2026-02-20 — si volvemos del Stripe Billing Portal,
      // SubscriptionReturnPage deja un flag en sessionStorage. Abrimos la
      // sección "Mi Suscripción" directamente en lugar de la principal.
      let openOnSubscription = false;
      try {
        if (sessionStorage.getItem('damyxia.openSubscriptionOnHome') === '1') {
          sessionStorage.removeItem('damyxia.openSubscriptionOnHome');
          openOnSubscription = true;
        }
      } catch { /* ignore */ }
      setActiveSection(openOnSubscription ? 'subscription' : 'main');
      checkAdminStatus();
      loadUserInfo();
    }
  }, [isOpen]);

  const loadUserInfo = async () => {
    try {
      const token = getToken();
      if (!token || token === 'guest') {
        setUserInfo({ email: 'guest@damyxia.com', avatar: null });
        return;
      }
      const response = await axios.get(`${API}/users/me`, {
      });
      setUserInfo({
        email: response.data.email || '',
        avatar: response.data.avatar || response.data.profile_image || null
      });
    } catch (error) {
      console.error('Error loading user info:', error);
      setUserInfo({ email: '', avatar: null });
    }
  };

  const checkAdminStatus = async () => {
    try {
      // CUADRO NEGRO FIX feb-2026: override permanente para las 3 cuentas
      // admin oficiales. Garantía 100% incluso si el token en memoria se perdió.
      const userEmail = (typeof window !== 'undefined' && localStorage.getItem('userEmail')) || '';
      if (isOwnerAdminEmail(userEmail)) {
        setIsAdmin(true);
        return;
      }

      const token = getToken();
      const headers = {};  // P2.1 cookie-only via axios.defaults
      const response = await axios.get(`${API}/stats/check-admin`, {
        headers,
        withCredentials: true,
      });
      setIsAdmin(response.data?.is_admin === true);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      if (saved) {
        setSettings(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      toast.success(t.saved);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleQuickAction = (action) => {
    // FAQ se abre como modal hijo sobre el propio SettingsModal — NO cerramos.
    if (action === 'faq') {
      setShowFAQ(true);
      return;
    }
    // Para la acción share, primero abrimos el modal de referidos
    // y luego cerramos settings con un pequeño delay
    if (action === 'share') {
      onOpenShare?.();
      setTimeout(() => onClose(), 200);
      return;
    }
    
    // Para otras acciones, cerramos primero y luego abrimos
    onClose();
    setTimeout(() => {
      switch (action) {
        case 'onboarding':
          resetOnboarding();
          break;
        case 'conversations':
          onOpenConversations?.();
          break;
        case 'profile':
          onOpenProfile?.();
          break;
        case 'memory':
          onOpenMemory?.();
          break;
        case 'learning':
          onOpenLearning?.();
          break;
        case 'insights':
          onOpenInsights?.();
          break;
        case 'historia':
          // 🟥 CUADRO NEGRO HistoriaDamyxia (feb-2026) — navegación
          // declarativa al dashboard de historia (sin navigate() manual,
          // usamos window.location para no requerir refactor del modal
          // para que use useNavigate de react-router).
          window.location.assign('/dashboard/historia');
          break;
        case 'admin':
          onOpenAdmin?.();
          break;
        case 'terms':
          onOpenTermsAdmin?.();
          break;
        default:
          break;
      }
    }, 100);
  };

  if (!isOpen) return null;

  const quickActions = [
    {
      id: 'onboarding',
      icon: BookOpen,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20',
      title: t.onboarding,
      description: t.onboardingDesc,
    },
    {
      id: 'profile',
      icon: User,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/20',
      title: t.profile,
      description: t.profileDesc,
    },
    {
      id: 'share',
      icon: Share2,
      color: 'text-indigo-400',
      bgColor: 'bg-indigo-500/20',
      title: t.share,
      description: t.shareDesc,
    },
    // 🟥 Tile "github" removido (Cuadro Negro 2026-05-30) — el acceso a
    // GitHub ahora se hace exclusivamente vía el botón del Header (OAuth).
    {
      id: 'memory',
      icon: Brain,
      color: 'text-violet-400',
      bgColor: 'bg-violet-500/20',
      title: t.memory,
      description: t.memoryDesc,
    },
    {
      id: 'learning',
      icon: GraduationCap,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/20',
      title: t.learning,
      description: t.learningDesc,
    },
    {
      id: 'insights',
      icon: Activity,
      color: 'text-rose-400',
      bgColor: 'bg-rose-500/20',
      title: t.insights,
      description: t.insightsDesc,
    },
    // 🟥 CUADRO NEGRO HistoriaDamyxia (feb-2026) — acceso desde el menú
    // de Settings al dashboard de historia narrativa (Insights v5).
    {
      id: 'historia',
      icon: BookOpen,
      color: 'text-amber-300',
      bgColor: 'bg-amber-500/20',
      title: 'Historia con DamyxIA',
      description: 'Tu línea de vida narrativa generada por IA',
    },
    {
      id: 'faq',
      icon: HelpCircle,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20',
      title: t.faq,
      description: t.faqDesc,
    },
    // Admin option REMOVIDA feb-2026 — Marcelo: ya está integrada en el Panel del Creador.
    // Solo dejamos Términos y Condiciones (gestión legal sí queda acá para admins).
    ...(isAdmin ? [{
      id: 'terms',
      icon: FileText,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20',
      title: t.terms,
      description: t.termsDesc,
    }] : []),
  ];

  const notificationItems = [
    {
      key: 'projectUpdates',
      icon: Bell,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
      title: t.projectUpdates,
      description: t.projectUpdatesDesc,
    },
    {
      key: 'newFeatures',
      icon: Sparkles,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/20',
      title: t.newFeatures,
      description: t.newFeaturesDesc,
    },
    {
      key: 'communityHighlights',
      icon: Users,
      color: 'text-green-400',
      bgColor: 'bg-green-500/20',
      title: t.communityHighlights,
      description: t.communityHighlightsDesc,
    },
    {
      key: 'securityAlerts',
      icon: Shield,
      color: 'text-red-400',
      bgColor: 'bg-red-500/20',
      title: t.securityAlerts,
      description: t.securityAlertsDesc,
    },
    {
      key: 'marketing',
      icon: Tag,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/20',
      title: t.marketing,
      description: t.marketingDesc,
    },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-5 bg-gradient-to-r from-zinc-800 to-zinc-900 border-b border-zinc-700">
          {/* 🟥 CUADRO NEGRO 2026-06-02 — Botón "Cerrar sesión" removido del
             modal de Configuración. Generaba confusión con "Regresar". El
             logout queda únicamente en el header de la app (zona segura). */}
          <div className="absolute top-3 right-3 flex items-center gap-1">
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
              data-testid="close-settings-modal"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-zinc-700 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-zinc-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t.title}</h2>
              <p className="text-sm text-zinc-400 mt-0.5">{t.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[55vh] overflow-y-auto">
          {activeSection === 'main' ? (
            <div className="space-y-4">
              {/* User Profile Section - Visible on mobile */}
              <div className="sm:hidden">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3 px-1">{t.userProfile}</p>
                <button
                  onClick={() => handleQuickAction('profile')}
                  className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-indigo-900/30 to-violet-900/30 hover:from-indigo-900/50 hover:to-violet-900/50 rounded-xl border border-indigo-500/30 hover:border-indigo-500/50 transition-all group"
                  data-testid="mobile-user-profile-section"
                >
                  {/* Avatar */}
                  <div className="relative">
                    {userInfo.avatar ? (
                      <img 
                        src={userInfo.avatar} 
                        alt="Avatar" 
                        className="w-14 h-14 rounded-full object-cover border-2 border-indigo-500/50"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-full flex items-center justify-center border-2 border-indigo-500/50">
                        <User className="w-7 h-7 text-white" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-zinc-900" />
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-semibold text-white text-base truncate">
                      {userInfo.email ? userInfo.email.split('@')[0] : 'Usuario'}
                    </p>
                    <p className="text-xs text-zinc-400 truncate">{userInfo.email || 'usuario@damyxia.com'}</p>
                    <p className="text-xs text-indigo-400 mt-1">{t.editProfile}</p>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-indigo-400 group-hover:text-indigo-300 transition-colors flex-shrink-0" />
                </button>
              </div>

              {/* Quick Actions */}
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3 px-1">{t.quickActions}</p>
                <div className="space-y-2">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.id}
                        onClick={() => handleQuickAction(action.id)}
                        className="w-full flex items-center gap-4 p-4 bg-zinc-800/50 hover:bg-zinc-800 rounded-xl border border-zinc-700/50 hover:border-zinc-600 transition-all group"
                        data-testid={`quick-action-${action.id}`}
                      >
                        <div className={`w-10 h-10 ${action.bgColor} rounded-lg flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${action.color}`} />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-white text-sm">{action.title}</p>
                          <p className="text-xs text-zinc-400">{action.description}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notifications Section */}
              <div>
                <button
                  onClick={() => setActiveSection('notifications')}
                  className="w-full flex items-center gap-4 p-4 bg-zinc-800/50 hover:bg-zinc-800 rounded-xl border border-zinc-700/50 hover:border-zinc-600 transition-all group"
                >
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-white text-sm">{t.notifications}</p>
                    <p className="text-xs text-zinc-400">{t.notificationsDesc}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </button>
              </div>

              {/* 🟥 CUADRO NEGRO 2026-02-20 — Mi Suscripción.
                  Posición: debajo de "Notificaciones". */}
              <div>
                <button
                  onClick={() => setActiveSection('subscription')}
                  data-testid="settings-my-subscription-btn"
                  className="w-full flex items-center gap-4 p-4 bg-zinc-800/50 hover:bg-zinc-800 rounded-xl border border-zinc-700/50 hover:border-zinc-600 transition-all group"
                >
                  <div className="w-10 h-10 bg-fuchsia-500/20 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-white text-sm">{t.mySubscription}</p>
                    <p className="text-xs text-zinc-400">{t.mySubscriptionDesc}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </button>
              </div>
            </div>
          ) : activeSection === 'subscription' ? (
            /* 🟥 CUADRO NEGRO 2026-02-20 — Mi Suscripción. */
            <MySubscriptionPanel
              language={language}
              onBack={() => setActiveSection('main')}
            />
          ) : (
            <div className="space-y-3">
              {/* Back button */}
              <button
                onClick={() => setActiveSection('main')}
                className="text-sm text-indigo-400 hover:text-indigo-300 mb-2 flex items-center gap-1"
              >
                ← {t.back}
              </button>
              
              {/* Push Notifications Section - NEW */}
              <div className="mb-4">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3 px-1">{t.pushNotifications}</p>
                <NotificationSettings compact={false} />
              </div>
              
              {/* Email Notifications Section */}
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3 px-1">{t.notifications}</p>
              
              {notificationItems.map((item) => {
                const Icon = item.icon;
                const isEnabled = settings[item.key];
                
                return (
                  <div 
                    key={item.key}
                    className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50"
                  >
                    <div className={`w-8 h-8 ${item.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${item.color}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm">{item.title}</p>
                      <p className="text-xs text-zinc-500 truncate">{item.description}</p>
                    </div>
                    
                    <button
                      onClick={() => handleToggle(item.key)}
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                        isEnabled ? 'bg-indigo-600' : 'bg-zinc-600'
                      }`}
                      data-testid={`toggle-${item.key}`}
                    >
                      <span 
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          isEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
              
              {/* Save button for notifications */}
              <button
                onClick={handleSave}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors"
                data-testid="save-notifications-btn"
              >
                <Save className="w-4 h-4" />
                {t.savePreferences}
              </button>
            </div>
          )}
        </div>

        {/* Footer Actions — botón "Regresar" (NO destructivo).
            Cerrar sesión se movió al header como link discreto rojo, para
            evitar cierres de sesión accidentales por proximidad al borde. */}
        {activeSection === 'main' && (
          <div className="flex items-center gap-3 px-4 py-4 bg-zinc-800/30 border-t border-zinc-700">
            <button
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-zinc-700/60 hover:bg-zinc-700 text-zinc-200 font-medium rounded-xl transition-colors"
              data-testid="settings-goback-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.goBack}
            </button>
          </div>
        )}
      </div>
      {/* FAQModal — Preguntas Frecuentes (se monta sobre el Settings sin cerrarlo) */}
      <FAQModal isOpen={showFAQ} onClose={() => setShowFAQ(false)} />
    </div>,
    document.body
  );
};

export default SettingsModal;
